import React from 'react';
import { X } from 'lucide-react';
import { RUBY, RUBY_DEEP } from '../../inventory/InventoryChrome';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { toastService } from '../../../services/toastService';
import { MigrationApiError, type MigrationJob, type MigrationStatus } from '../../../services/migrationApi';

/** Shared bits for Tools → Data Migration (list + job workspace). Ruby on light. */

export { RUBY, RUBY_DEEP };

export const SOURCES: { id: string; label: string }[] = [
  { id: 'square', label: 'Square' },
  { id: 'clover', label: 'Clover' },
  { id: 'lightspeed_x', label: 'Lightspeed X' },
  { id: 'lightspeed_r', label: 'Lightspeed R' },
  { id: 'toast', label: 'Toast' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'other', label: 'Other / unknown' },
];
export const sourceLabel = (s?: string) => (s ? SOURCES.find((x) => x.id === s)?.label || s : '—');

export type StatusGroup = 'action' | 'progress' | 'done';

export const STATUS_META: Record<MigrationStatus, { label: string; cls: string; group: StatusGroup }> = {
  uploading: { label: 'Uploading', cls: 'bg-stone-100 text-stone-600', group: 'progress' },
  submitted: { label: 'Submitted', cls: 'bg-amber-50 text-amber-800', group: 'action' },
  analysing: { label: 'Analysing', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  mapped: { label: 'Mapped', cls: 'bg-amber-50 text-amber-800', group: 'action' },
  dry_run_ready: { label: 'Dry run ready', cls: 'bg-amber-50 text-amber-800', group: 'action' },
  approved: { label: 'Approved', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  backing_up: { label: 'Backing up', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  applying: { label: 'Applying', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  stocking: { label: 'Setting stock', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  applied: { label: 'Applied', cls: 'bg-green-50 text-green-700', group: 'done' },
  rolling_back: { label: 'Rolling back', cls: 'bg-sky-50 text-sky-700', group: 'progress' },
  rolled_back: { label: 'Rolled back', cls: 'bg-stone-200 text-stone-700', group: 'done' },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700', group: 'action' },
};

/** Statuses where the backend worker is busy — the workspace polls while in one. */
export const BUSY_STATUSES: MigrationStatus[] = ['analysing', 'backing_up', 'applying', 'stocking', 'rolling_back'];
export const ANALYSABLE: MigrationStatus[] = ['uploading', 'submitted', 'mapped', 'dry_run_ready', 'failed'];
export const EDITABLE: MigrationStatus[] = ['mapped', 'dry_run_ready'];

export const dryRunRunning = (job?: MigrationJob | null) =>
  !!job?.dryRunRequestedAt && (!job.dryRunAt || job.dryRunAt < job.dryRunRequestedAt);

export const StatusPill: React.FC<{ status: MigrationStatus; needsInput?: boolean }> = ({ status, needsInput }) => {
  const m = STATUS_META[status] || { label: status, cls: 'bg-stone-100 text-stone-600' };
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${m.cls}`}>{m.label.toUpperCase()}</span>
      {needsInput && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 whitespace-nowrap">WAITING ON CLIENT</span>}
    </span>
  );
};

export function fmtDateTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function fmtBytes(n?: number): string {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const fmtMoney = (n?: number) => (typeof n === 'number' ? n.toFixed(2) : '—');

/** Toast an API failure — a 409 carries the backend's "not allowed now" reason. */
export function toastApiError(e: unknown, fallback: string) {
  if (e instanceof MigrationApiError && e.status === 409) toastService.warning(e.message);
  else toastService.error((e as any)?.message || fallback);
}

// ── Buttons ─────────────────────────────────────────────────────────────────
type BtnKind = 'black' | 'ruby' | 'outline' | 'danger';
export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; busy?: boolean; icon?: React.ReactNode }> = ({
  kind = 'outline', busy, icon, children, className = '', disabled, style, ...rest
}) => {
  const base = 'h-11 px-4 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
  const kinds: Record<BtnKind, string> = {
    black: 'bg-stone-900 text-white hover:bg-black',
    ruby: 'text-white',
    outline: 'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50',
    danger: 'border border-rose-300 bg-white text-rose-700 hover:bg-rose-50',
  };
  return (
    <button
      type="button"
      disabled={disabled || busy}
      className={`${base} ${kinds[kind]} ${className}`}
      style={kind === 'ruby' ? { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})`, ...style } : style}
      {...rest}
    >
      {busy ? <RubyGemMark size={16} drawing color="currentColor" /> : icon}
      {children}
    </button>
  );
};

// ── Custom switch (never a native checkbox) — 44px hit area ─────────────────
export const Switch: React.FC<{ on: boolean; busy?: boolean; disabled?: boolean; onClick: () => void; label: string }> = ({ on, busy, disabled, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy || disabled}
    onClick={onClick}
    className="h-11 w-14 flex items-center justify-center shrink-0 disabled:opacity-60"
  >
    <span className="w-12 h-7 rounded-full relative transition-colors" style={{ background: on ? RUBY : '#d6d3d1' }}>
      <span
        className="w-6 h-6 bg-white rounded-full absolute top-0.5 shadow flex items-center justify-center transition-all"
        style={{ left: on ? 'calc(100% - 26px)' : '2px', color: RUBY }}
      >
        {busy && <RubyGemMark size={14} drawing color="currentColor" />}
      </span>
    </span>
  </button>
);

/** Custom checkbox (never native) — 44px row. */
export const CheckRow: React.FC<{ checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }> = ({ checked, onChange, children }) => (
  <button type="button" role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)} className="min-h-[44px] flex items-center gap-2 text-sm text-stone-700 text-left">
    <span
      className="w-5 h-5 rounded border grid place-items-center shrink-0"
      style={checked ? { background: RUBY, borderColor: RUBY } : { borderColor: '#a8a29e', background: '#fff' }}
    >
      {checked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6.5l2.5 2.5L10 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </span>
    <span>{children}</span>
  </button>
);

/** Chip choice row (sources, filters). */
export const Chips: React.FC<{ options: { id: string; label: string; count?: number }[]; value: string; onChange: (id: string) => void }> = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = o.id === value;
      return (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`h-11 px-4 rounded-full text-sm font-medium border transition ${on ? 'text-white border-transparent' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
          style={on ? { background: '#1c1917' } : undefined}
        >
          {o.label}{typeof o.count === 'number' && <span className={`ml-1.5 text-xs ${on ? 'text-stone-300' : 'text-stone-400'}`}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

// ── Modal: black header + ruby underline + ruby icon tile, white body ───────
export const Modal: React.FC<{
  title: string;
  icon: React.FC<any>;
  onClose: () => void;
  busy?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}> = ({ title, icon: Icon, onClose, busy, children, footer, wide }) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50" onClick={busy ? undefined : onClose}>
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} overflow-hidden max-h-[90vh] flex flex-col`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-black">
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(220,20,60,0.18)', color: '#fb7185' }}>
            <Icon size={18} />
          </div>
          <h3 className="flex-1 font-semibold text-white truncate">{title}</h3>
          <button type="button" onClick={onClose} disabled={busy} className="h-11 w-11 -mr-3 grid place-items-center text-stone-400 hover:text-white disabled:opacity-50" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, ${RUBY_DEEP})`, height: '3px' }} />
      </div>
      <div className="p-5 overflow-y-auto text-stone-700">{children}</div>
      {footer && <div className="px-5 py-4 bg-stone-50 border-t border-stone-100 flex justify-end gap-2 flex-wrap">{footer}</div>}
    </div>
  </div>
);

export const Card: React.FC<{ title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, actions, children, className = '' }) => (
  <section className={`bg-white rounded-xl border border-stone-200 ${className}`}>
    {(title || actions) && (
      <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-3 flex-wrap">
        <h2 className="flex-1 font-semibold text-stone-900">{title}</h2>
        {actions}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const StatTile: React.FC<{ label: string; value?: number | string; tone?: 'ruby' | 'amber' | 'plain' }> = ({ label, value, tone = 'plain' }) => (
  <div className="bg-white rounded-xl border border-stone-200 px-4 py-3 min-w-0">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 truncate">{label}</div>
    <div className={`text-xl font-semibold mt-0.5 ${tone === 'ruby' ? 'text-[#DC143C]' : tone === 'amber' ? 'text-amber-700' : 'text-stone-900'}`}>
      {value ?? '—'}
    </div>
  </div>
);

export const Banner: React.FC<{ tone: 'amber' | 'rose' | 'sky' | 'green'; children: React.ReactNode; icon?: React.ReactNode }> = ({ tone, children, icon }) => {
  const cls = {
    amber: 'border-amber-300 bg-amber-50 text-amber-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-900',
    green: 'border-green-200 bg-green-50 text-green-800',
  }[tone];
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-2 ${cls}`}>
      {icon && <span className="shrink-0 mt-0.5">{icon}</span>}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};
